import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { AlertTriangle, Search } from 'lucide-react';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatChf, formatDateShort } from '@/lib/format';
import { toast } from 'sonner';

export const Route = createFileRoute('/transactions')({
  component: TransactionsPage,
});

function TransactionsPage() {
  const qc = useQueryClient();
  const [filters, setFilters] = useState<{ q: string; categoryId?: string; offset: number; uncatOnly: boolean }>(
    { q: '', offset: 0, uncatOnly: false },
  );
  const limit = 50;

  const { data: cats } = useQuery({ queryKey: ['categories'], queryFn: api.listCategories });
  const { data, isLoading } = useQuery({
    queryKey: ['transactions', filters],
    queryFn: () =>
      api.listTransactions({
        q: filters.q || undefined,
        category_ids: filters.categoryId,
        uncategorized_only: filters.uncatOnly,
        limit,
        offset: filters.offset,
      }),
  });

  const setCategoryMut = useMutation({
    mutationFn: ({ id, categoryId }: { id: string; categoryId: string | null }) =>
      api.setCategoryOnTx(id, categoryId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['transactions'] });
      toast.success('Catégorie mise à jour');
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Transactions</h1>
        <div className="text-sm text-muted-foreground">
          {data ? `${data.total} mouvements` : '…'}
        </div>
      </div>

      <Card>
        <CardContent className="flex flex-wrap items-center gap-3 p-4">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Rechercher contrepartie ou description…"
              value={filters.q}
              onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value, offset: 0 }))}
            />
          </div>
          <Select
            value={filters.categoryId ?? ''}
            onValueChange={(v) => setFilters((f) => ({ ...f, categoryId: v || undefined, offset: 0 }))}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Toutes catégories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="">Toutes catégories</SelectItem>
              {(cats ?? []).map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant={filters.uncatOnly ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilters((f) => ({ ...f, uncatOnly: !f.uncatOnly, offset: 0 }))}
          >
            Non catégorisées
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 text-left">Date</th>
                  <th className="px-4 py-2 text-left">Description</th>
                  <th className="px-4 py-2 text-left">Contrepartie</th>
                  <th className="px-4 py-2 text-left">Catégorie</th>
                  <th className="px-4 py-2 text-right">Montant</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">
                      Chargement…
                    </td>
                  </tr>
                ) : (
                  (data?.rows ?? []).map((row) => (
                    <tr key={row.tx.id} className="border-t border-border hover:bg-muted/30">
                      <td className="px-4 py-2 num-tnum">{formatDateShort(row.tx.bookingDate)}</td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-2">
                          {row.tx.isAnomaly ? <AlertTriangle className="h-4 w-4 text-warning" /> : null}
                          <span className="truncate max-w-[40ch]" title={row.tx.description}>
                            {row.tx.description}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-2 text-muted-foreground">{row.tx.counterparty ?? '—'}</td>
                      <td className="px-4 py-2">
                        <Select
                          value={row.tx.categoryId ?? ''}
                          onValueChange={(v) => setCategoryMut.mutate({ id: row.tx.id, categoryId: v || null })}
                        >
                          <SelectTrigger className="h-8 w-[180px]">
                            <SelectValue placeholder="—">
                              {row.category ? (
                                <span className="flex items-center gap-2">
                                  <span
                                    className="h-2 w-2 rounded-full"
                                    style={{ background: row.category.color ?? 'hsl(var(--muted-fg))' }}
                                  />
                                  {row.category.name}
                                </span>
                              ) : null}
                              {!row.category && row.tx.categoryConfidence !== null && row.tx.categoryConfidence < 0.6 ? (
                                <Badge variant="warning" className="text-[10px]">À vérifier</Badge>
                              ) : null}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {(cats ?? []).map((c) => (
                              <SelectItem key={c.id} value={c.id}>
                                {c.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className={`px-4 py-2 text-right num-tnum font-medium ${Number(row.tx.amountChf) < 0 ? 'text-destructive' : 'text-success'}`}>
                        {formatChf(row.tx.amountChf)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between border-t border-border p-3">
            <Button
              variant="outline"
              size="sm"
              disabled={filters.offset === 0}
              onClick={() => setFilters((f) => ({ ...f, offset: Math.max(0, f.offset - limit) }))}
            >
              Précédent
            </Button>
            <span className="text-xs text-muted-foreground">
              {filters.offset + 1}–{Math.min((data?.total ?? 0), filters.offset + limit)} / {data?.total ?? 0}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={(data?.total ?? 0) <= filters.offset + limit}
              onClick={() => setFilters((f) => ({ ...f, offset: f.offset + limit }))}
            >
              Suivant
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
